import { createPortal } from 'react-dom'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'

/** One capability pill. Shared by the inline chip and the popover list so the two can never drift apart. */
const PILL_CLASS = 'inline-flex items-center rounded-full bg-default px-1.5 py-px text-[10px] font-medium leading-tight text-default-foreground'
/** A pill that gives up width to its neighbours — inline, when a `+N` popover holds the full text. */
const CHIP_CLASS = `${PILL_CLASS} min-w-0 max-w-full truncate`
/** A pill that keeps its own width — when nothing is behind `+N`, so a cut-off chip would have nowhere to be
 *  read in full. Still capped: the dock height is imported free text, and an uncapped long answer would widen
 *  the Vehicle column and scroll the whole table. Every offered value fits; a longer one ellipsises, and the
 *  chip's title carries it in full. */
const WHOLE_CHIP_CLASS = `${PILL_CLASS} max-w-[10rem] shrink-0 truncate`

const POPOVER_HEADING_CLASS = 'px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted'

interface VehicleCapabilityChipsProps {
    /** What the vehicle can DO (lift-gate, pallet jack, …). Empty/absent renders nothing. */
    capabilities: string[] | null | undefined
    /** The vehicle's Dock Height / Ramps answer, already formatted. Rendered first, as "Dock: …", so it is
     *  never the one hidden behind `+N`. */
    dockHeight?: string | null
}

/**
 * The capability chips under the vehicle type, on ONE line whatever the count.
 *
 * A wrapped chip list made the row as tall as the driver with the most equipment — a truck with four
 * capabilities pushed its row to three times the height of its neighbours, and the Nearby Drivers table is
 * read by scanning DOWN it. So only the first chip is inline; the rest collapse into a `+N` button that
 * opens the full set in a popover. The popover is portaled and fixed-positioned, so opening it moves no row.
 *
 * The popover lists EVERY capability, the inline one included: an operator who opened it is reading for a
 * specific capability and should not have to combine two places to get the whole list.
 */
export const VehicleCapabilityChips = ({ capabilities, dockHeight }: VehicleCapabilityChipsProps) => {
    // Called before the empty-list early return — hook order has to hold across renders.
    const { open, containerRef, triggerRef, panelRef, togglePanel, panelPositionStyle } = useAnchoredPopover({ panelWidth: 220 })

    if (!capabilities?.length && !dockHeight) return null
    const hiddenCount = capabilities?.length ? capabilities.length - 1 : 0
    // Dock height plus at most one capability: no `+N`, so no popover to read a truncated chip in. Each keeps its
    // own width (up to its cap) and the column widens for them; the shared 11rem cap applies only when a popover
    // holds the rest.
    const inlineChipClass = hiddenCount > 0 ? CHIP_CLASS : WHOLE_CHIP_CLASS

    return (
        <div ref={containerRef} className={`relative mt-0.5 flex items-center gap-1 ${hiddenCount > 0 ? 'max-w-[11rem] overflow-hidden' : ''}`}>
            {dockHeight && (
                <span className={inlineChipClass} title={`Dock height / Ramps: ${dockHeight}`}>
                    Dock: {dockHeight}
                </span>
            )}
            {capabilities?.length ? (
                <span className={inlineChipClass} title={capabilities[0]}>
                    {capabilities[0]}
                </span>
            ) : null}
            {hiddenCount > 0 && (
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={togglePanel}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    title={capabilities?.join(', ')}
                    className="shrink-0 rounded-full bg-accent-soft px-1.5 py-px text-[10px] font-semibold leading-tight text-accent-soft-foreground transition-colors duration-150 hover:bg-accent-soft-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    +{hiddenCount}
                </button>
            )}
            {open &&
                createPortal(
                    <div ref={panelRef} style={panelPositionStyle} className="rounded-xl bg-surface p-2 shadow-overlay">
                        {/* In full here: inline, beside the first capability, the dock chip is usually cut off. */}
                        {dockHeight && (
                            <>
                                <div className={POPOVER_HEADING_CLASS}>Dock height / Ramps</div>
                                <div className="mb-2 flex">
                                    <span className={CHIP_CLASS}>{dockHeight}</span>
                                </div>
                            </>
                        )}
                        <div className={POPOVER_HEADING_CLASS}>Equipment</div>
                        <div className="flex flex-wrap gap-1">
                            {capabilities?.map((capability) => (
                                <span key={capability} className={CHIP_CLASS}>
                                    {capability}
                                </span>
                            ))}
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
